/**
 * Maneuvers Configuration System
 * 
 * Provides context-based configuration for maneuvers components to support
 * different data sources (dataset, fleet, historical) with configurable
 * table columns, color options, and API endpoints.
 */

import * as d3 from 'd3';
import { apiEndpoints } from '@config/env';
import { persistantStore } from '../store/persistantStore';
import { sourcesStore } from '../store/sourcesStore';
import { getColorByIndex } from './colorScale';

export type ManeuversContext = 'dataset' | 'fleet' | 'historical';

export interface TableColumnConfig {
  columns: string[];
  descriptions: string[];
  channels: string[];
}

export interface ManeuversConfig {
  context: ManeuversContext;
  apiEndpoints: {
    table: string;
    map: string;
    timeSeries: string;
  };
  buildQueryParams: (baseParams: {
    className: string;
    projectId: number;
    eventType: string;
    description?: string;
    eventList?: number[];
  }) => Record<string, any>;
  colorOptions: string[];
  getTableColumns: (phase: string, color: string, eventType?: string) => TableColumnConfig;
  getColorScale: (colorName: string, data: any[]) => d3.ScaleLinear<number, string> | d3.ScaleThreshold<number, string> | d3.ScaleOrdinal<string | number, string>;
}

/**
 * Get table column configuration based on phase and color
 */
function getDatasetTableColumns(phase: string, color: string, className?: string, eventType?: string): TableColumnConfig {
  // Base columns - TACK will be replaced if color is MAINSAIL, HEADSAIL, or RACE
  let secondColumn = 'TACK';
  let secondDesc = 'TACK';
  let secondChan = 'twa_entry';

  // Phase-specific loss channel (tgt = loss vs target VMG)
  let lossChannel = 'loss_total_tgt';
  let lossDesc = 'LOSS TOTAL [M]';
  if (phase === 'INVESTMENT') {
    lossChannel = 'loss_inv_tgt';
    lossDesc = 'INVESTMENT LOSS [M]';
  } else if (phase === 'TURN') {
    lossChannel = 'loss_turn_tgt';
    lossDesc = 'TURN LOSS [M]';
  } else if (phase === 'ACCELERATION') {
    lossChannel = 'loss_build_tgt';
    lossDesc = 'BUILD LOSS [M]';
  }

  // Replace TACK with color-specific column if color is MAINSAIL, HEADSAIL, RACE, STATE, CONFIG, YEAR, or EVENT
  // When color is RACE we still show RACE in its own column and use TACK as second column (see below)
  if (color === 'RACE') {
    secondColumn = 'RACE';
    secondDesc = 'RACE';
    secondChan = 'Race_number';
  } else if (color === 'STATE') {
    secondColumn = 'STATE';
    secondDesc = 'STATE';
    secondChan = 'State';
  } else if (color === 'CONFIG') {
    secondColumn = 'CONFIG';
    secondDesc = 'CONFIG';
    secondChan = 'Config';
  } else if (color === 'CREW') {
    secondColumn = 'CREW';
    secondDesc = 'CREW';
    secondChan = 'Crew';
  } else if (color === 'TESTS') {
    secondColumn = 'TESTS';
    secondDesc = 'TESTS';
    secondChan = 'Tests';
  } else if (color === 'YEAR') {
    secondColumn = 'YEAR';
    secondDesc = 'YEAR';
    secondChan = 'Year';
  } else if (color === 'EVENT') {
    secondColumn = 'EVENT';
    secondDesc = 'EVENT';
    secondChan = 'Event';
  }

  // Determine TWS unit based on class name
  const twsUnit = className?.toLowerCase() === 'gp50' ? '[kph]' : '[kts]';
  const twsDesc = `TWS ${twsUnit}`;

  // Dataset: always show RACE (race number) after DATETIME; when color is RACE use TACK as second data column to avoid duplicate
  const secondCol = color === 'RACE' ? 'TACK' : secondColumn;
  const secondDescOut = color === 'RACE' ? 'TACK' : secondDesc;
  const secondChanOut = color === 'RACE' ? 'twa_entry' : secondChan;

  if (isTakeoffEventType(eventType)) {
    const takeoff = getTakeoffSmallTableMetrics();
    return {
      columns: ['DATETIME', 'RACE', secondCol, twsDesc, ...takeoff.map((m) => m.column)],
      descriptions: ['DATETIME', 'RACE', secondDescOut, twsDesc, ...takeoff.map((m) => m.description)],
      channels: ['Datetime', 'Race_number', secondChanOut, 'tws_avg', ...takeoff.map((m) => m.channel)]
    };
  }

  return {
    columns: ['DATETIME', 'RACE', secondCol, twsDesc, lossDesc, 'MMG', 'VMG%'],
    descriptions: ['DATETIME', 'RACE', secondDescOut, twsDesc, lossDesc, 'MMG', 'VMG% OF TARGET'],
    channels: ['Datetime', 'Race_number', secondChanOut, 'tws_avg', lossChannel, 'mmg', 'vmg_perc_avg']
  };
}

/**
 * Get table column configuration for fleet context
 */
function getFleetTableColumns(phase: string, color: string, className?: string, eventType?: string): TableColumnConfig {
  // Base columns - TACK will be replaced if color is MAINSAIL, HEADSAIL, RACE, or SOURCE
  let secondColumn = 'TACK';
  let secondDesc = 'TACK';
  let secondChan = 'twa_entry';

  // Phase-specific loss channel (tgt = loss vs target VMG)
  let lossChannel = 'loss_total_tgt';
  let lossDesc = 'LOSS TOTAL [M]';
  if (phase === 'INVESTMENT') {
    lossChannel = 'loss_inv_tgt';
    lossDesc = 'INVESTMENT LOSS [M]';
  } else if (phase === 'TURN') {
    lossChannel = 'loss_turn_tgt';
    lossDesc = 'TURN LOSS [M]';
  } else if (phase === 'ACCELERATION') {
    lossChannel = 'loss_build_tgt';
    lossDesc = 'BUILD LOSS [M]';
  }

  // Replace TACK with color-specific column if color is MAINSAIL, HEADSAIL, RACE, SOURCE, STATE, CONFIG, YEAR, or EVENT
  if (color === 'SOURCE') {
    secondColumn = 'SOURCE';
    secondDesc = 'SOURCE';
    secondChan = 'source_name';
  } else if (color === 'RACE') {
    secondColumn = 'RACE';
    secondDesc = 'RACE';
    secondChan = 'Race_number';
  } else if (color === 'STATE') {
    secondColumn = 'STATE';
    secondDesc = 'STATE';
    secondChan = 'State';
  } else if (color === 'CONFIG') {
    secondColumn = 'CONFIG';
    secondDesc = 'CONFIG';
    secondChan = 'Config';
  } else if (color === 'CREW') {
    secondColumn = 'CREW';
    secondDesc = 'CREW';
    secondChan = 'Crew';
  } else if (color === 'TESTS') {
    secondColumn = 'TESTS';
    secondDesc = 'TESTS';
    secondChan = 'Tests';
  } else if (color === 'YEAR') {
    secondColumn = 'YEAR';
    secondDesc = 'YEAR';
    secondChan = 'Year';
  } else if (color === 'EVENT') {
    secondColumn = 'EVENT';
    secondDesc = 'EVENT';
    secondChan = 'Event';
  }

  // Determine TWS unit based on class name
  const twsUnit = className?.toLowerCase() === 'gp50' ? '[kph]' : '[kts]';
  const twsDesc = `TWS ${twsUnit}`;

  // Fleet: always show RACE (race number) after DATETIME; when color is RACE use TACK as second data column to avoid duplicate
  const secondCol = color === 'RACE' ? 'TACK' : secondColumn;
  const secondDescOut = color === 'RACE' ? 'TACK' : secondDesc;
  const secondChanOut = color === 'RACE' ? 'twa_entry' : secondChan;

  if (isTakeoffEventType(eventType)) {
    const takeoff = getTakeoffSmallTableMetrics();
    return {
      columns: ['DATETIME', 'RACE', secondCol, twsDesc, ...takeoff.map((m) => m.column)],
      descriptions: ['DATETIME', 'RACE', secondDescOut, twsDesc, ...takeoff.map((m) => m.description)],
      channels: ['Datetime', 'Race_number', secondChanOut, 'tws_avg', ...takeoff.map((m) => m.channel)]
    };
  }

  return {
    columns: ['DATETIME', 'RACE', secondCol, twsDesc, lossDesc, 'MMG', 'VMG%'],
    descriptions: ['DATETIME', 'RACE', secondDescOut, twsDesc, lossDesc, 'MMG', 'VMG% OF TARGET'],
    channels: ['Datetime', 'Race_number', secondChanOut, 'tws_avg', lossChannel, 'mmg', 'vmg_perc_avg']
  };
}

const ACCMAX_TOOLTIP = 'Value at time of maximum acceleration.';
const ROUNDUP_TURN_TOOLTIP = 'Value at maneuver climax (TWA crossing).';

const ROUNDUP_BEARAWAY_CLIMAX_CHANNEL_DEFS = [
  { channel: 'Cant_accmax', short: 'Cant' },
  { channel: 'Wing_ca1_accmax', short: 'Wing Ca1' },
  { channel: 'Wing_twist_accmax', short: 'Wing Twist' },
  { channel: 'Jib_lead_ang_accmax', short: 'Jib Lead' },
] as const;

/** TAKEOFF-specific columns for big table and scatter (channel -> header; description = tooltip). Tws_bin first so it is always requested and shown for takeoff. */
export const TAKEOFF_TABLE_COLUMNS: { channel: string; header: string; description?: string }[] = [
  { channel: 'Tws_bin', header: 'TWS BIN', description: 'Wind speed bin (KPH)' },
  { channel: 'Tws_avg', header: 'TWS [KPH]', description: 'Average true wind speed' },
  { channel: 'Mmg', header: 'Mmg', description: 'Meters made good in direction of wind for duration of takeoff' },
  { channel: 'Vmg_perc_avg', header: 'Vmg%', description: 'Average VMG% of target for duration of takeoff' },
  { channel: 'Loss_total_tgt', header: 'Loss total tgt', description: 'Overall maneuver loss in meters relative to target VMG' },
  { channel: 'Bsp_start', header: 'Start Bsp', description: 'Initial boat speed' },
  // abs_<column> is the server alias for abs() of exactly that column. Twa_entry needs it because
  // the plain twa_entry channel is remapped to Twa_start for TAKEOFF (it feeds the Port/Stbd column).
  { channel: 'abs_Twa_start', header: 'Start Twa', description: 'True wind angle at the start of the takeoff.' },
  { channel: 'abs_Twa_entry', header: 'Entry Twa', description: 'True wind angle at maneuver entry.' },
  { channel: 'Bsp_min', header: 'Min Bsp', description: 'Minimum boat speed before the acceleration' },
  { channel: 'Bsp_max', header: 'Max Bsp', description: 'Maximum boat speed over the takeoff window' },
  { channel: 'Bsp_build', header: 'Build Bsp', description: 'Average boat speed over the build phase: from reaching 28 KPH to the end of the takeoff window' },
  { channel: 'Twa_build_n', header: 'Build Twa', description: 'Average true wind angle over the same build phase as Build Bsp.'  },
  { channel: 'Time_accel', header: 'Accel Time', description: 'Seconds accelerating from minimum boat speed to pop onto foils' },
  { channel: 'Exit_time', header: 'Pop Time', description: 'Time from reaching 28 KPH of boat speed to popping onto foils' },
  // Exit_time already owns the "Pop Time" header, so the 32 KPH instant is "Build Time".
  { channel: 'Pop_time', header: 'Build Time', description: 'Time from reaching 28 KPH of boat speed to reaching 32 KPH. Blank when the takeoff never got there (graded 1).' },
  { channel: 'Bsp_exit', header: 'Pop Bsp', description: 'Boat speed at time of pop onto foils.' },
  { channel: 'Twa_exit', header: 'Pop Twa', description: 'True wind angle at time of pop onto foils.' },
  { channel: 'abs_Twa_final', header: 'Final Twa', description: 'True wind angle at the end of the takeoff.' },
  // Accel_max / Twa_accmax_n lead the ACCMAX block, mirroring ACCELERATION_ACCMAX_COLUMNS. Neither
  // name ends in "_accmax", so neither is picked up by the filter that builds that array below.
  { channel: 'Accel_max', header: 'ACCMAX Accel', description: 'Maximum acceleration rate [m/s²].' },
  { channel: 'Twa_accmax_n', header: 'ACCMAX Twa', description: ACCMAX_TOOLTIP },
  { channel: 'Pitch_accmax', header: 'ACCMAX Pitch', description: ACCMAX_TOOLTIP },
  { channel: 'Heel_accmax', header: 'ACCMAX Heel', description: ACCMAX_TOOLTIP },
  { channel: 'Rake_accmax', header: 'ACCMAX DB Rake', description: ACCMAX_TOOLTIP },
  { channel: 'Rud_rake_accmax', header: 'ACCMAX Rud Rake', description: ACCMAX_TOOLTIP },
  { channel: 'Rud_diff_accmax', header: 'ACCMAX Rud Diff', description: ACCMAX_TOOLTIP },
  { channel: 'Cant_accmax', header: 'ACCMAX Cant', description: ACCMAX_TOOLTIP },
  { channel: 'Jib_sheet_pct_accmax', header: 'ACCMAX Jib Sht%', description: ACCMAX_TOOLTIP },
  { channel: 'Jib_lead_ang_accmax', header: 'ACCMAX Jib Lead', description: ACCMAX_TOOLTIP },
  { channel: 'Jib_cunno_load_accmax', header: 'ACCMAX Jib Cunno', description: ACCMAX_TOOLTIP },
  { channel: 'Wing_clew_pos_accmax', header: 'ACCMAX Wing Clew', description: ACCMAX_TOOLTIP },
  { channel: 'Wing_twist_accmax', header: 'ACCMAX Wing Twist', description: ACCMAX_TOOLTIP },
  { channel: 'Wing_ca1_accmax', header: 'ACCMAX Wing Ca1', description: ACCMAX_TOOLTIP }
];

/** Channels to request from API when event type is TAKEOFF (only takeoff-relevant columns). */
export const TAKEOFF_CHANNELS: string[] = TAKEOFF_TABLE_COLUMNS.map((c) => c.channel);

/** True when the selected maneuver event type is TAKEOFF. */
export function isTakeoffEventType(eventType: string | null | undefined): boolean {
  return String(eventType ?? '').trim().toUpperCase() === 'TAKEOFF';
}

/**
 * The three metric columns the small (summary) table shows for TAKEOFF, in place of the
 * loss / MMG / VMG% trio.
 *
 * Loss, MMG and VMG% describe how much ground a turn gave away; a takeoff is judged on how quickly
 * it got up and away. The phase loss channels are not even requested for takeoff
 * (they are absent from TAKEOFF_CHANNELS and from all four server-side takeoff allowlists), so that
 * column was blank on every phase but FULL.
 *
 * The trio is the two takeoff instants plus the speed at the first one: Exit_time (the pop onto
 * foils), Bsp_exit (speed there) and Pop_time (when the boat reached 32 KPH). Both times are
 * seconds from sec = 0, the 28 KPH crossing. Pop_time is NULL on a takeoff that never got there,
 * which the small table's cell renderer prints blank.
 *
 * Header and tooltip are read back out of TAKEOFF_TABLE_COLUMNS so the small and big tables cannot
 * drift apart. The channel is lowercased because the API aliases every column to lowercase and the
 * small table's cell renderer looks up `row[channel]` with no case fallback.
 */
const TAKEOFF_SMALL_TABLE_CHANNELS = ['Exit_time', 'Bsp_exit', 'Pop_time'] as const;

export function getTakeoffSmallTableMetrics(): { column: string; description: string; channel: string }[] {
  return TAKEOFF_SMALL_TABLE_CHANNELS.map((channel) => {
    const def = TAKEOFF_TABLE_COLUMNS.find((c) => c.channel === channel);
    if (!def) { throw new Error(`TAKEOFF_TABLE_COLUMNS has no entry for ${channel}`); }
    return {
      column: def.header.toUpperCase(),
      description: def.description ?? def.header,
      channel: channel.toLowerCase(),
    };
  });
}

/** TWS columns kept in the takeoff table but dropped from scatter/box plots: the x-axis is already TWS. */
const TAKEOFF_SCATTER_OMIT = new Set(['tws_bin', 'tws_avg']);

/** Scatter/box-plot channels for TAKEOFF: the big-table list without the TWS columns. */
export const TAKEOFF_SCATTER_CHANNELS: string[] = TAKEOFF_TABLE_COLUMNS
  .map((c) => c.channel.toLowerCase())
  .filter((c) => !TAKEOFF_SCATTER_OMIT.has(c));

/** ACCMAX channels shown in big table / scatter when phase is ACCELERATION (tack/gybe etc.). */
export const ACCELERATION_ACCMAX_COLUMNS: { channel: string; header: string; description?: string }[] = [
  { channel: 'Bsp_accmax', header: 'ACCMAX Bsp', description: ACCMAX_TOOLTIP },
  { channel: 'Twa_accmax', header: 'ACCMAX Twa', description: ACCMAX_TOOLTIP },
  ...TAKEOFF_TABLE_COLUMNS.filter((c) => c.channel.toLowerCase().endsWith('_accmax')),
];

/** Channels to request from API for acceleration-phase accmax metrics. */
export const ACCELERATION_ACCMAX_CHANNELS: string[] = ACCELERATION_ACCMAX_COLUMNS.map((c) => c.channel);

/** Build-phase VMG channels for acceleration table / scatter. */
export const ACCELERATION_BUILD_VMG_CHANNELS: string[] = ['Vmg_perc_build', 'Vmg_build_avg'];

/** Ride height metrics from maneuver_stats: min/max/avg pooled over the maneuver window, plus value at max acceleration. */
export const RIDE_HEIGHT_CHANNELS: string[] = ['Rh_avg', 'Rh_min', 'Rh_max', 'Rh_accmax'];

/** Ride height scatter channels (lowercase API aliases). */
export const RIDE_HEIGHT_SCATTER_CHANNELS: string[] = RIDE_HEIGHT_CHANNELS.map((c) => c.toLowerCase());

/** Metric columns for ACCELERATION phase big table (excludes datetime/race/tack/tws/color). */
export const ACCELERATION_TABLE_METRICS: { column: string; description: string; channel: string }[] = [
  { column: 'BUILD LOSS', description: 'BUILD IS 5 SECONDS AFTER HEAD TO WIND TO 20 SECONDS AFTER', channel: 'loss_build_tgt' },
  { column: 'VMG% BUILD', description: 'VMG% OF TARGET FOR THE BUILD PHASE', channel: 'vmg_perc_build' },
  { column: 'VMG BUILD', description: 'AVERAGE VMG DURING THE BUILD PHASE [KPH]', channel: 'vmg_build_avg' },
  { column: 'BS MIN', description: 'BS MIN [KPH]', channel: 'bsp_min' },
  { column: 'TWA EXIT', description: 'TWA AT MAXIMUM TURN ANGLE', channel: 'twa_exit_n' },
];

/** Scatter/boxplot channel order for ACCELERATION phase (matches big table metrics + accmax). */
export const ACCELERATION_SCATTER_CHANNELS: string[] = [
  ...ACCELERATION_TABLE_METRICS.map((m) => m.channel),
  ...ACCELERATION_ACCMAX_CHANNELS,
  ...RIDE_HEIGHT_SCATTER_CHANNELS,
];

/** Entry-angle metric added for roundup/bearaway, which report entry/exit TWA rather than drop TWA. */
const ROUNDUP_BEARAWAY_TWA_ENTRY_METRIC = {
  column: 'TWA ENTRY',
  description: 'TWA AT MANEUVER ENTRY',
  channel: 'twa_entry_n',
};

/** ACCELERATION-phase big table metrics for the selected event type (roundup/bearaway add TWA ENTRY). */
export function getAccelerationTableMetrics(
  eventType: string | null | undefined
): { column: string; description: string; channel: string }[] {
  const metrics = [...ACCELERATION_TABLE_METRICS];
  if (!isRoundupBearawayEventType(eventType)) {
    return metrics;
  }
  const exitIndex = metrics.findIndex((m) => m.channel === 'twa_exit_n');
  metrics.splice(exitIndex < 0 ? metrics.length : exitIndex, 0, { ...ROUNDUP_BEARAWAY_TWA_ENTRY_METRIC });
  return metrics;
}

/** ACCELERATION-phase scatter/boxplot channels for the selected event type. */
export function getAccelerationScatterChannels(eventType: string | null | undefined): string[] {
  return [
    ...getAccelerationTableMetrics(eventType).map((m) => m.channel),
    ...ACCELERATION_ACCMAX_CHANNELS,
    ...RIDE_HEIGHT_SCATTER_CHANNELS,
  ];
}

/** True when maneuver type uses entry/max speed metrics instead of drop-button timing fields. */
export function isRoundupBearawayEventType(eventType: string | null | undefined): boolean {
  const e = String(eventType ?? '').trim().toUpperCase();
  return e === 'ROUNDUP' || e === 'BEARAWAY';
}

/** Roundup/bearaway climax channel labels — TURN for roundups, ACCMAX for bearaways. */
export function getRoundupBearawayClimaxColumns(
  eventType: string | null | undefined
): { channel: string; header: string; description: string }[] {
  const isRoundup = String(eventType ?? '').trim().toUpperCase() === 'ROUNDUP';
  const prefix = isRoundup ? 'TURN' : 'ACCMAX';
  const description = isRoundup ? ROUNDUP_TURN_TOOLTIP : ACCMAX_TOOLTIP;
  return ROUNDUP_BEARAWAY_CLIMAX_CHANNEL_DEFS.map(({ channel, short }) => ({
    channel,
    header: `${prefix} ${short}`,
    description,
  }));
}

/** Bearaway accmax columns (roundups use TURN labels via getRoundupBearawayClimaxColumns). */
export const ROUNDUP_BEARAWAY_ACCMAX_COLUMNS: { channel: string; header: string; description: string }[] =
  getRoundupBearawayClimaxColumns('BEARAWAY');

export const ROUNDUP_BEARAWAY_ACCMAX_SUMMARY_CHANNELS: string[] = ROUNDUP_BEARAWAY_ACCMAX_COLUMNS.map((c) =>
  c.channel.toLowerCase()
);

/**
 * Tack/gybe metric channel keys for Summary Tables.
 * Matches Fleet Cheat Sheet Maneuvers section (group by Channel).
 */
export const TACK_GYBE_CHEAT_SHEET_METRIC_CHANNELS: string[] = [
  'bsp_drop',
  'twa_drop',
  'drop_time',
  'turn_rate_max',
  'bsp_min',
  'bsp_min_delta',
  'twa_exit',
  'turn_angle_max',
  'rake_aoa_min_old_turn',
  'overshoot_angle',
  'raise_time',
  'time_two_boards',
  'twa_build',
  'loss_inv_tgt',
  'loss_turn_tgt',
  'loss_build_tgt',
  'loss_total_tgt',
  'vmg_perc_avg',
];

/**
 * Roundup/bearaway metric channel keys for Summary Tables / cheat sheets.
 * Matches getBigTableColumns() metric order (after TWS; row label is separate).
 * Exception: lwy_max is big-table only — the summary/cheat-sheet SQL aggregates a fixed metric list
 * (see getManeuverCheatSheet_TableData) that has no leeway column.
 */
export const ROUNDUP_BEARAWAY_SUMMARY_METRIC_CHANNELS: string[] = [
  'loss_inv_tgt',
  'loss_turn_tgt',
  'loss_build_tgt',
  'loss_total_tgt',
  'mmg',
  'vmg_perc_avg',
  'bsp_entry',
  'bsp_max',
  'bsp_min',
  'bsp_min_delta',
  'turn_rate_max',
  'turn_radius',
  'twa_entry',
  'twa_exit',
  'time_two_boards',
  ...ROUNDUP_BEARAWAY_ACCMAX_SUMMARY_CHANNELS,
];

/**
 * API channel names omitted for roundup/bearaway (tack/gybe-only fields).
 * These are never written by roundups.py / bearaways.py, so requesting them only pulls NULLs.
 * Names are the DB spellings — see maneuverChannelAliases.ts for the rake_* display aliases.
 */
const ROUNDUP_BEARAWAY_OMIT_API_CHANNELS = new Set([
  'Bsp_drop',
  'Drop_time',
  'Pop_time',
  'Raise_time',
  'Twa_drop_n',
  'Overshoot_perc',
  'Accel_slope',
  'Decel_slope',
  'Time_dropping',
  'Time_raising',
  'Heel_lock',
  'Rake_min_old_turn',
  'Rake_max_new_turn',
  'Rake_aoa_min_old_turn',
  'Rake_removed_old_time_turn',
  'Rake_added_new_time_turn',
  'Aoa_raise',
  'Cant_drop_tgt',
]);

/** Default scatter channel order for tack/gybe maneuvers. */
export const TACK_GYBE_SCATTER_CHANNELS: string[] = [
  'mmg',
  'vmg_perc_avg',
  'loss_total_tgt',
  'loss_inv_tgt',
  'loss_turn_tgt',
  'loss_build_tgt',
  'bsp_drop',
  'bsp_min',
  'bsp_min_delta',
  'twa_drop_n',
  'overshoot_perc',
  'turn_angle_max',
  'turn_rate_max',
  'rud_ang_max',
  'lwy_max',
  'decel_slope',
  'accel_slope',
  'turn_radius',
  'time_two_boards',
  'time_dropping',
  'time_raising',
  'pop_time',
  'drop_time',
  'raise_time',
  'heel_lock',
  'rake_min_old_turn',
  'rake_max_new_turn',
  'rake_aoa_min',
  'rake_removed_time',
  'rake_added_time',
  'aoa_raise',
  'cant_drop_tgt',
  ...RIDE_HEIGHT_SCATTER_CHANNELS,
];

/**
 * Scatter channel order for roundup/bearaway — mirrors getBigTableColumns() roundup/bearaway metrics.
 * Entry/exit TWA instead of drop TWA / overshoot; no drop-button or foil-stow timing fields
 * (roundups.py / bearaways.py never write them).
 */
export const ROUNDUP_BEARAWAY_SCATTER_CHANNELS: string[] = [
  'mmg',
  'vmg_perc_avg',
  'loss_total_tgt',
  'loss_inv_tgt',
  'loss_turn_tgt',
  'loss_build_tgt',
  'bsp_entry',
  'bsp_max',
  'bsp_min',
  'bsp_min_delta',
  'twa_entry_n',
  'twa_exit_n',
  'turn_angle_max',
  'turn_rate_max',
  'turn_radius',
  'rud_ang_max',
  'lwy_max',
  'time_two_boards',
  ...ROUNDUP_BEARAWAY_ACCMAX_SUMMARY_CHANNELS,
  ...RIDE_HEIGHT_SCATTER_CHANNELS,
];

/**
 * Maneuver table-data channels for the selected event type.
 * Roundup/bearaway swap drop-button fields for entry/max speed columns.
 */
export function getManeuversChannelsForEventType(
  eventType: string,
  baseChannels: readonly string[]
): string[] {
  const upper = String(eventType ?? 'TACK').trim().toUpperCase();
  if (upper === 'TAKEOFF') {
    return [...TAKEOFF_CHANNELS];
  }
  if (isRoundupBearawayEventType(upper)) {
    const filtered = baseChannels.filter((c) => !ROUNDUP_BEARAWAY_OMIT_API_CHANNELS.has(c));
    for (const extra of ['Bsp_entry', 'Bsp_max', 'Twa_entry_n', 'Twa_exit_n']) {
      if (!filtered.some((x) => x.toLowerCase() === extra.toLowerCase())) {
        filtered.push(extra);
      }
    }
    return filtered;
  }
  return [...baseChannels];
}

/**
 * Channels JSON for maneuvers API requests (includes twa_entry prefix for takeoff pages).
 */
export function buildManeuversChannelsRequest(
  eventType: string,
  baseChannels: readonly string[],
  options?: { takeoffPrefix?: string[] }
): string[] {
  const upper = String(eventType ?? 'TACK').trim().toUpperCase();
  if (upper === 'TAKEOFF') {
    return [...(options?.takeoffPrefix ?? []), ...TAKEOFF_CHANNELS];
  }
  return getManeuversChannelsForEventType(eventType, baseChannels);
}

/** Scatter/boxplot channels for the selected event type and phase. */
export function getManeuverScatterChannelsForEventType(
  eventType: string,
  phaseValue: string
): string[] {
  const upper = String(eventType ?? 'TACK').trim().toUpperCase();
  if (upper === 'TAKEOFF') {
    return [...TAKEOFF_SCATTER_CHANNELS];
  }
  if (phaseValue === 'ACCELERATION') {
    return getAccelerationScatterChannels(upper).map((c) => c.toLowerCase());
  }
  if (isRoundupBearawayEventType(upper)) {
    return [...ROUNDUP_BEARAWAY_SCATTER_CHANNELS];
  }
  return [...TACK_GYBE_SCATTER_CHANNELS];
}

/**
 * Get table column configuration for DataTable_Big (full table)
 */
export function getBigTableColumns(
  context: ManeuversContext,
  color: string,
  className?: string,
  eventType?: string,
  phase?: string
): TableColumnConfig {
  const isTakeoff = (eventType || '').toUpperCase() === 'TAKEOFF';

  if (isTakeoff) {
    // TAKEOFF: basics (Datetime, Race, Tack, Source) + TAKEOFF-specific columns
    const baseColumns = ['DATETIME', 'RACE', 'TACK'];
    const baseDescriptions = ['DATETIME', 'RACE', 'TACK'];
    const baseChannels = ['Datetime', 'Race_number', 'twa_entry'];
    const sourceColumn = ['SOURCE'];
    const sourceDesc = ['SOURCE'];
    const sourceChan = ['source_name'];

    const takeoffColumns = TAKEOFF_TABLE_COLUMNS.map((c) => c.header);
    const takeoffDescriptions = TAKEOFF_TABLE_COLUMNS.map((c) => c.description ?? c.header);
    const takeoffChannels = TAKEOFF_TABLE_COLUMNS.map((c) => c.channel);

    return {
      columns: [...baseColumns, ...sourceColumn, ...takeoffColumns],
      descriptions: [...baseDescriptions, ...sourceDesc, ...takeoffDescriptions],
      channels: [...baseChannels, ...sourceChan, ...takeoffChannels]
    };
  }

  // Fleet and dataset: RACE (race number) after DATETIME
  const baseColumns = ['DATETIME', 'RACE', 'TACK'];
  const baseDescriptions = ['DATETIME', 'RACE', 'TACK'];
  const baseChannels = ['Datetime', 'Race_number', 'twa_entry'];

  // Color-specific column (RACE already in base, so don't add again when color is RACE)
  let colorColumn: string[] = [];
  let colorDesc: string[] = [];
  let colorChan: string[] = [];

  if (color === 'RACE') {
    // RACE already in base columns
  } else if (context === 'fleet' && color === 'SOURCE') {
    colorColumn = ['SOURCE'];
    colorDesc = ['SOURCE'];
    colorChan = ['source_name'];
  } else if (context === 'fleet' && color === 'CONFIG') {
    colorColumn = ['CONFIG'];
    colorDesc = ['CONFIG'];
    colorChan = ['Config'];
  } else if (context === 'fleet' && color === 'CREW') {
    colorColumn = ['CREW'];
    colorDesc = ['CREW'];
    colorChan = ['Crew'];
  } else if (context === 'fleet' && color === 'TESTS') {
    colorColumn = ['TESTS'];
    colorDesc = ['TESTS'];
    colorChan = ['Tests'];
  } else if (context === 'fleet' && color === 'STATE') {
    colorColumn = ['STATE'];
    colorDesc = ['STATE'];
    colorChan = ['State'];
  } else if (color === 'STATE') {
    colorColumn = ['STATE'];
    colorDesc = ['STATE'];
    colorChan = ['State'];
  } else if (color === 'CONFIG') {
    colorColumn = ['CONFIG'];
    colorDesc = ['CONFIG'];
    colorChan = ['Config'];
  } else if (color === 'CREW') {
    colorColumn = ['CREW'];
    colorDesc = ['CREW'];
    colorChan = ['Crew'];
  } else if (color === 'TESTS') {
    colorColumn = ['TESTS'];
    colorDesc = ['TESTS'];
    colorChan = ['Tests'];
  } else if (color === 'YEAR') {
    colorColumn = ['YEAR'];
    colorDesc = ['YEAR'];
    colorChan = ['Year'];
  } else if (color === 'EVENT') {
    colorColumn = ['EVENT'];
    colorDesc = ['EVENT'];
    colorChan = ['Event'];
  }

  // Determine TWS unit based on class name
  const twsUnit = className?.toLowerCase() === 'gp50' ? '[kph]' : '[kts]';
  const twsDesc = `TWS ${twsUnit}`;

  const fullColumns = [
    ...baseColumns,
    ...colorColumn,
    twsDesc,
    'INV LOSS',
    'TURN LOSS',
    'BUILD LOSS',
    'TOTAL LOSS',
    'MMG',
    'VMG%',
    'BSP DROP',
    'TWA DROP',
    'BS MIN',
    'BS MIN Δ',
    'TURN RATE',
    'LWY MAX',
    'TURN RADIUS',
    'TWA EXIT',
    'OS ANG',
    'DROP TIME',
    'POP TIME',
    'RAISE TIME',
    'TWO BOARD TIME'
  ];

  const fullDescriptions = [
    ...baseDescriptions,
    ...colorDesc,
    twsDesc,
    'INVESTMENT IS 15 TO 5 SECONDS BEFORE HEAD TO WIND',
    'TURN IS 5 SECONDS BEFORE HEAD TO WIND TO 5 SECONDS AFTER',
    'BUILD IS 5 SECONDS AFTER HEAD TO WIND TO 20 SECONDS AFTER',
    'BASELINE VMG FOR WIND BIN vs. AVERAGE VMG FOR MANEUVER',
    'METERS MADE GOOD IN DIRECTION OF WIND',
    'VMG% OF TARGET [%]',
    'SPEED AT BUTTON PRESS [KPH]',
    'TWA AT BUTTON PRESS [DEG]',
    'BS MIN [KPH]',
    'BS DROP - BS MIN [KPH]',
    'MAX TURN RATE [DEG/SEC]',
    'LEEWAY MAX [DEG]',
    'TURN RADIUS [M]',
    'TWA AT MAXIMUM TURN ANGLE',
    'MAXIMUM TURN ANGLE - FINAL HEADING',
    'TIME IN SECONDS OF BUTTON PRESS (MINUS = BEFORE HEAD TO WIND)',
    'TIME IN SECONDS OF WING INVERSION CA1 = CA6 (MINUS = BEFORE HEAD TO WIND)',
    'TIME IN SECONDS OF BUTTON PRESS (MINUS = BEFORE HEAD TO WIND)',
    'SECONDS WITH BOTH FOILS > 1.2 M EXTENSION'
  ];

  const fullChannels = [
    ...baseChannels,
    ...colorChan,
    'tws_avg',
    'loss_inv_tgt',
    'loss_turn_tgt',
    'loss_build_tgt',
    'loss_total_tgt',
    'mmg',
    'vmg_perc_avg',
    'bsp_drop',
    'twa_drop_n',
    'bsp_min',
    'bsp_min_delta',
    'turn_rate_max',
    'lwy_max',
    'turn_radius',
    'twa_exit_n',
    'overshoot_angle',
    'drop_time',
    'pop_time',
    'raise_time',
    'Time_two_boards'
  ];

  if (phase === 'ACCELERATION') {
    const accmaxHeaders = ACCELERATION_ACCMAX_COLUMNS.map((c) => c.header);
    const accmaxDescriptions = ACCELERATION_ACCMAX_COLUMNS.map((c) => c.description ?? c.header);
    const accmaxChannels = ACCELERATION_ACCMAX_COLUMNS.map((c) => c.channel);
    const accelerationMetrics = getAccelerationTableMetrics(eventType);
    return {
      columns: [
        ...baseColumns,
        ...colorColumn,
        twsDesc,
        ...accelerationMetrics.map((m) => m.column),
        ...accmaxHeaders,
      ],
      descriptions: [
        ...baseDescriptions,
        ...colorDesc,
        twsDesc,
        ...accelerationMetrics.map((m) => m.description),
        ...accmaxDescriptions,
      ],
      channels: [
        ...baseChannels,
        ...colorChan,
        'tws_avg',
        ...accelerationMetrics.map((m) => m.channel),
        ...accmaxChannels,
      ],
    };
  }

  if (isRoundupBearawayEventType(eventType)) {
    const rbClimaxColumns = getRoundupBearawayClimaxColumns(eventType);
    const rbAccmaxHeaders = rbClimaxColumns.map((c) => c.header);
    const rbAccmaxDescriptions = rbClimaxColumns.map((c) => c.description);
    const rbAccmaxChannels = rbClimaxColumns.map((c) => c.channel);

    const rbColumns = [
      ...baseColumns,
      ...colorColumn,
      twsDesc,
      'INV LOSS',
      'TURN LOSS',
      'BUILD LOSS',
      'TOTAL LOSS',
      'MMG',
      'VMG%',
      'BSP ENTRY',
      'MAX BSP',
      'BS MIN',
      'BS MIN Δ',
      'TURN RATE',
      'LWY MAX',
      'TURN RADIUS',
      'TWA ENTRY',
      'TWA EXIT',
      'TWO BOARD TIME',
      ...rbAccmaxHeaders,
    ];
    const rbDescriptions = [
      ...baseDescriptions,
      ...colorDesc,
      twsDesc,
      'INVESTMENT IS 15 TO 5 SECONDS BEFORE HEAD TO WIND',
      'TURN IS 5 SECONDS BEFORE HEAD TO WIND TO 5 SECONDS AFTER',
      'BUILD IS 5 SECONDS AFTER HEAD TO WIND TO 20 SECONDS AFTER',
      'BASELINE VMG FOR WIND BIN vs. AVERAGE VMG FOR MANEUVER',
      'METERS MADE GOOD IN DIRECTION OF WIND',
      'VMG% OF TARGET [%]',
      'SPEED AT MANEUVER ENTRY [KPH]',
      'MAXIMUM BOAT SPEED DURING MANEUVER [KPH]',
      'BS MIN [KPH]',
      'BS ENTRY - BS MIN [KPH]',
      'MAX TURN RATE [DEG/SEC]',
      'LEEWAY MAX [DEG]',
      'TURN RADIUS [M]',
      'TWA AT MANEUVER ENTRY',
      'TWA AT MANEUVER EXIT',
      'SECONDS WITH BOTH FOILS > 1.2 M EXTENSION',
      ...rbAccmaxDescriptions,
    ];
    const rbChannels = [
      ...baseChannels,
      ...colorChan,
      'tws_avg',
      'loss_inv_tgt',
      'loss_turn_tgt',
      'loss_build_tgt',
      'loss_total_tgt',
      'mmg',
      'vmg_perc_avg',
      'bsp_entry',
      'bsp_max',
      'bsp_min',
      'bsp_min_delta',
      'turn_rate_max',
      'lwy_max',
      'turn_radius',
      'twa_entry_n',
      'twa_exit_n',
      'Time_two_boards',
      ...rbAccmaxChannels,
    ];
    return {
      columns: rbColumns,
      descriptions: rbDescriptions,
      channels: rbChannels,
    };
  }

  return {
    columns: fullColumns,
    descriptions: fullDescriptions,
    channels: fullChannels
  };
}

/**
 * Build color scale based on context and color name
 */
function buildColorScale(context: ManeuversContext, colorName: string, data: any[]): d3.ScaleLinear<number, string> | d3.ScaleThreshold<number, string> | d3.ScaleOrdinal<string | number, string> {
  if (colorName === 'TWS') {
    // Fixed TWS bins: 10, 15, 20, 25, 30, 35, 40, 45, 50
    // Use scaleThreshold with 8 thresholds to create 9 color ranges (one for each bin)
    const twsScale = d3.scaleThreshold<number, string>();
    twsScale.domain([12.5, 17.5, 22.5, 27.5, 32.5, 37.5, 42.5, 47.5]);
    twsScale.range(["blue", "lightblue", "cyan", "lightgreen", "yellow", "orange", "red", "darkred", "purple"]);
    return twsScale;
  } else if (colorName === 'VMG') {
    const nums: number[] = data.map((d: any) => {
      const val = d.vmg_perc_avg ?? d.vmg_perc_avg;
      return Number(val);
    }).filter((v): v is number => !isNaN(v));
    const min = nums.length ? d3.min(nums)! : 0;
    const max = nums.length ? d3.max(nums)! : 1;
    const mid = (min + max) / 2;
    const vmgScale = d3.scaleLinear<number, string>();
    vmgScale.domain([min, mid, max]);
    (vmgScale as any).range(["blue", "lightgrey", "red"]);
    return vmgScale;
  } else if (colorName === 'TACK') {
    return d3.scaleThreshold<number, string>().domain([-180, -1, 1, 180]).range(["red", "red", "#64ed64", "#64ed64"]);
  } else if (colorName === 'SOURCE' && context === 'fleet') {
    // Use fleet source colors
    const sources = sourcesStore.sources();
    if (sources.length === 0) {
      // Fallback to index-based if sources not loaded
      const sourceNames = Array.from(new Set(data.map((d: any) => d.source_name || 'Unknown').filter(Boolean)));
      const colors = sourceNames.map((_, i) => getColorByIndex(i));
      return d3.scaleOrdinal<string | number, string>().domain(sourceNames).range(colors);
    }
    const sourceNames = sources.map(s => String(s.source_name).toLowerCase());
    const colors = sources.map(s => s.color || '#1f77b4');
    const ordinalScale = d3.scaleOrdinal<string | number, string>().domain(sourceNames).range(colors);
    (ordinalScale as any).unknown('#1f77b4');
    return ordinalScale;
  } else if (colorName === 'RACE' || colorName === 'STATE' || colorName === 'CONFIG' || colorName === 'CREW' || colorName === 'TESTS' || colorName === 'YEAR' || colorName === 'EVENT') {
    const values = data.map((d: any) => {
      if (colorName === 'RACE') return d.race ?? d.Race_number;
      if (colorName === 'STATE') return d.State ?? d.state ?? d.STATE;
      if (colorName === 'CONFIG') return d.Config ?? d.config ?? d.CONFIG;
      if (colorName === 'CREW') return d.Crew ?? d.crew ?? d.CREW ?? d.CREW_CODE;
      if (colorName === 'TESTS') return d.Tests ?? d.tests ?? d.TESTS ?? d.TEST ?? 'NONE';
      if (colorName === 'YEAR') return d.Year ?? d.year ?? d.YEAR;
      if (colorName === 'EVENT') return d.Event ?? d.event ?? d.EVENT;
      return '';
    }).filter(v => v !== undefined && v !== null);
    const cats = Array.from(new Set(values as (string | number)[]));
    // Sort categorical values consistently
    const sortedCats = cats.sort((a, b) => {
      const aStr = String(a);
      const bStr = String(b);
      return aStr.localeCompare(bStr);
    });
    const colors: string[] = [];
    let i = 0;
    sortedCats.forEach(v => {
      if (colorName === 'RACE' && String(v) === '0') {
        colors.push('lightgrey');
      } else {
        colors.push(getColorByIndex(i));
      }
      i += 1;
    });
    return d3.scaleOrdinal<string | number, string>().domain(sortedCats).range(colors);
  } else {
    // Default fallback
    const defaultScale = d3.scaleLinear<number, string>();
    defaultScale.domain([8, 16, 28, 36, 44]);
    //defaultScale.domain([4, 8, 14, 18, 22]);
    (defaultScale as any).range(["yellow", "orange", "red"]);
    return defaultScale;
  }
}

/**
 * Get maneuvers configuration for a specific context
 */
export function getManeuversConfig(context: ManeuversContext = 'dataset'): ManeuversConfig {
  const { selectedClassName, selectedDatasetId, selectedSourceId, selectedDate } = persistantStore;

  if (context === 'fleet') {
    return {
      context: 'fleet',
      apiEndpoints: {
        table: `${apiEndpoints.app.data}/fleet-maneuvers-table-data`,
        // Map and time series use the unified maneuvers endpoints with explicit event_list
        // This works across all sources by filtering on event_list
        map: `${apiEndpoints.app.data}/maneuvers-map-data`,
        timeSeries: `${apiEndpoints.app.data}/maneuvers-timeseries-data`
      },
      buildQueryParams: (baseParams) => {
        // For map/time series endpoints: use event_list (works across all sources)
        // The regular maneuvers-map-data endpoint supports event_list filtering
        if (baseParams.eventList !== undefined && Array.isArray(baseParams.eventList)) {
          return {
            class_name: baseParams.className,
            project_id: baseParams.projectId,
            desc: baseParams.description || '',
            event_list: JSON.stringify(baseParams.eventList)
          };
        }
        // Table data path (no eventList) - uses source_id and date
        return {
          class_name: baseParams.className,
          project_id: baseParams.projectId,
          source_id: selectedSourceId(),
          date: selectedDate(),
          event_type: baseParams.eventType,
          ...(baseParams.description ? { desc: baseParams.description } : {})
        };
      },
      colorOptions: ['TWS', 'VMG', 'TACK', 'SOURCE', 'CONFIG', 'EVENT', 'TESTS'],
      getTableColumns: (phase: string, color: string, eventType?: string) => getFleetTableColumns(phase, color, selectedClassName(), eventType),
      getColorScale: (colorName: string, data: any[]) => buildColorScale('fleet', colorName, data)
    };
  } else if (context === 'historical') {
    // Historical uses new simplified endpoints
    return {
      context: 'historical',
      apiEndpoints: {
        table: `${apiEndpoints.app.data}/maneuvers-history`,
        map: `${apiEndpoints.app.data}/maneuvers-map-data`,
        timeSeries: `${apiEndpoints.app.data}/maneuvers-timeseries-data`
      },
      buildQueryParams: (baseParams) => {
        if (baseParams.eventList && baseParams.eventList.length > 0) {
          return {
            class_name: baseParams.className,
            project_id: baseParams.projectId,
            ...(baseParams.description ? { desc: baseParams.description } : {}),
            event_list: JSON.stringify(baseParams.eventList)
          };
        }
        return {
          class_name: baseParams.className,
          project_id: baseParams.projectId,
          source_id: selectedSourceId(),
          date: selectedDate(),
          event_type: baseParams.eventType,
          ...(baseParams.description ? { desc: baseParams.description } : {})
        };
      },
      colorOptions: ['TWS', 'VMG', 'TACK', 'SOURCE', 'CONFIG', 'CREW', 'EVENT', 'TESTS'],
      getTableColumns: (phase: string, color: string, eventType?: string) => getFleetTableColumns(phase, color, selectedClassName(), eventType),
      getColorScale: (colorName: string, data: any[]) => buildColorScale('historical', colorName, data)
    };
  } else {
    // Default: dataset
    return {
      context: 'dataset',
      apiEndpoints: {
        table: `${apiEndpoints.app.data}/maneuvers-table-data`,
        map: `${apiEndpoints.app.data}/maneuvers-map-data`,
        timeSeries: `${apiEndpoints.app.data}/maneuvers-timeseries-data`
      },
      buildQueryParams: (baseParams) => {
        // For map/time series we expect an explicit eventList; for table we still use dataset_id/event_type
        // If eventList is provided (even if empty), use it for map/time series endpoints
        // The calling function should validate that eventList is non-empty before calling
        if (baseParams.eventList !== undefined) {
          return {
            class_name: baseParams.className,
            project_id: baseParams.projectId,
            ...(baseParams.description ? { desc: baseParams.description } : {}),
            event_list: JSON.stringify(baseParams.eventList)
          };
        }
        // Table data path (no eventList)
        return {
          class_name: baseParams.className,
          project_id: baseParams.projectId,
          dataset_id: selectedDatasetId(),
          event_type: baseParams.eventType,
          ...(baseParams.description ? { desc: baseParams.description } : {})
        };
      },
      colorOptions: ['TWS', 'VMG', 'TACK', 'CONFIG', 'CREW', 'RACE', 'STATE', 'TESTS'],
      getTableColumns: (phase: string, color: string, eventType?: string) => getDatasetTableColumns(phase, color, selectedClassName(), eventType),
      getColorScale: (colorName: string, data: any[]) => buildColorScale('dataset', colorName, data)
    };
  }
}

